import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribeToCart } from '../../services/cartService';
import {
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Box,
  Button,
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import HowItWorksModal from '../HowItWorksModal';
import PromiseStrip from './PromiseStrip';

const StoreHeader = ({ children }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [howOpen, setHowOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToCart((items) => {
      setCartCount(Array.isArray(items) ? items.length : 0);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 960); // md breakpoint for Material-UI
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCartClick = () => {
    navigate('/cart');
  };

  const handleTitleClick = () => {
    navigate('/');
  };

  // The header Create action opens the "how it works" explainer first (per the approved design),
  // then "Get started" enters the wizard.
  const handleCreateClick = () => {
    setHowOpen(true);
  };

  const handleMyCreationsClick = () => {
    navigate('/creations');
  };

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: '#206884', // IMO Teal
          color: 'white',
          boxShadow: '0px 3px 10px 0px rgba(125,125,125,0.2)',
        }}
      >
        <Toolbar sx={{ minHeight: '80px !important', gap: 1.5 }}>
          <Box
            onClick={handleTitleClick}
            sx={{
              flexGrow: 1,
              minWidth: 0, // allow the logo to shrink instead of forcing the row to overflow
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              '&:hover': { opacity: 0.9 },
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/logo-mark-white.svg`}
              // Decorative — the wordmark beside it carries the accessible name, so the lockup is
              // announced once rather than twice.
              alt=""
              sx={{ height: { xs: 38, md: 54 }, width: 'auto', flexShrink: 0, display: 'block' }}
            />
            {/* The wordmark is back on phones. The revised row carries the cart and nothing else on
                mobile (the thumb-zone bar owns the primary action), so the space the old
                "My creations" + pill + free-shipping tab needed is free. */}
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/logo-wordmark-white.svg`}
              alt="The Card Store"
              sx={{ height: { xs: 21, md: 32 }, width: 'auto', display: 'block' }}
            />
          </Box>

          {children}

          {/* Desktop nav: a quiet text link back to saved work, and the primary action as a solid
              white pill — the one filled action on the surface, so it reads as the offer rather than
              as another piece of chrome. On mobile both drop out; StoreMobileActionBar carries the
              primary action into the thumb zone instead. */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
              <Button
                onClick={handleMyCreationsClick}
                sx={{
                  color: 'rgba(255,255,255,0.92)',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  px: 1.25,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    color: '#fff',
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                Your cards
              </Button>
              <Button
                onClick={handleCreateClick}
                startIcon={<AutoAwesomeIcon sx={{ fontSize: 20 }} />}
                sx={{
                  color: '#206884',
                  backgroundColor: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  borderRadius: '22px',
                  px: 2.25,
                  py: 1.125,
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: '#f2f8fa' },
                }}
              >
                Design your own card
              </Button>
            </Box>
          )}

          <IconButton
            onClick={handleCartClick}
            aria-label="Cart"
            sx={{
              color: 'white',
              p: 1,
              ml: 1,
              flexShrink: 0,
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            {/* The free-delivery tab that used to hang off this icon now lives in the promise strip
                below, where it is legible and appears on every page. */}
            <Badge
              badgeContent={cartCount}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#4d7a2c',
                  color: 'white',
                  fontWeight: 600,
                },
              }}
            >
              <CartIcon sx={{ fontSize: 26 }} />
            </Badge>
          </IconButton>
        </Toolbar>
        <HowItWorksModal open={howOpen} onClose={() => setHowOpen(false)} />
      </AppBar>
      <PromiseStrip />
    </>
  );
};

export default StoreHeader;
